<template>
    <div class="order-detail-modal">
        <div class="modal-header border-0 pb-0">
            <h5 class="modal-title">
                <i class="fas fa-receipt mr-2"></i>
                Order {{ order ? shortRef(order.order_id) : '...' }}
            </h5>
            <button type="button" class="close" @click="$emit('close')"><span>&times;</span></button>
        </div>

        <div class="modal-body" v-if="loading">
            <div class="text-center py-4"><div class="spinner-border spinner-border-sm"></div> Memuat...</div>
        </div>

        <div class="modal-body" v-else-if="order">
            <div class="row mb-3">
                <div class="col-6">
                    <small class="text-muted d-block">Status Order</small>
                    <span :class="statusBadge(order.status)">{{ statusLabel(order.status) }}</span>
                </div>
                <div class="col-6">
                    <small class="text-muted d-block">Pembayaran</small>
                    <span :class="paymentBadge(order.payment_status)">{{ paymentLabel(order.payment_status) }}</span>
                </div>
            </div>

            <div class="info-grid mb-3">
                <div><small class="text-muted">Total</small><div class="font-weight-bold">{{ formatRupiah(order.total_amount) }}</div></div>
                <div v-if="order.shop_owner_amount"><small class="text-muted">Saldo Net</small><div class="text-success">{{ formatRupiah(order.shop_owner_amount) }}</div></div>
                <div v-if="order.payment_method"><small class="text-muted">Metode</small><div>{{ order.payment_method }}</div></div>
                <div v-if="order.transaction_id"><small class="text-muted">Ref Tripay</small><div><code class="small">{{ order.transaction_id }}</code></div></div>
            </div>

            <h6 class="mt-3">Customer</h6>
            <p class="mb-1"><b>{{ order.customer_name }}</b></p>
            <p class="mb-1 small text-muted"><i class="fab fa-whatsapp"></i> {{ order.customer_phone }}</p>
            <p class="mb-1 small text-muted" v-if="order.customer_email"><i class="fas fa-envelope"></i> {{ order.customer_email }}</p>
            <p class="mb-2 small" v-if="order.shipping_address"><i class="fas fa-map-marker-alt"></i> {{ order.shipping_address }}</p>

            <h6 class="mt-3">Item Pesanan</h6>
            <table class="table table-sm table-borderless mb-0" v-if="items.length">
                <tbody>
                    <tr v-for="item in items" :key="item.id">
                        <td>{{ item.product_name }} <span class="text-muted">x{{ item.quantity }}</span></td>
                        <td class="text-right">{{ formatRupiah(item.subtotal) }}</td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr><td class="text-right text-muted">Ongkir</td><td class="text-right">{{ formatRupiah(order.shipping_cost) }}</td></tr>
                    <tr><td class="text-right"><b>Total</b></td><td class="text-right"><b>{{ formatRupiah(order.total_amount) }}</b></td></tr>
                </tfoot>
            </table>
            <p v-else class="text-muted small">Tidak ada item</p>

            <div v-if="order.shipping_courier" class="mt-3 p-2 bg-light rounded">
                <small class="text-muted">Pengiriman</small>
                <div>{{ order.shipping_courier }} {{ order.shipping_service || '' }}</div>
                <div>Resi: <b>{{ order.shipping_tracking || '-' }}</b></div>
            </div>

            <div class="timeline mt-3 small text-muted">
                <div>Dibuat: {{ formatTime(order.created_at) }}</div>
                <div v-if="order.paid_at">Dibayar: {{ formatTime(order.paid_at) }}</div>
                <div v-if="order.shipped_at">Dikirim: {{ formatTime(order.shipped_at) }}</div>
                <div v-if="order.completed_at">Selesai: {{ formatTime(order.completed_at) }}</div>
            </div>

            <hr>
            <div class="action-buttons">
                <button v-if="order.payment_status === 'pending'" class="btn btn-success btn-sm btn-block mb-2" @click="markPaid">
                    <i class="fas fa-check"></i> Konfirmasi Pembayaran
                </button>
                <button v-if="order.payment_status === 'paid' && order.status === 'pending'" class="btn btn-info btn-sm btn-block text-white mb-2" @click="updateStatus('processed')">
                    <i class="fas fa-box"></i> Tandai Diproses
                </button>
                <button v-if="order.payment_status === 'paid' && ['pending','processed','processing'].includes(order.status)" class="btn btn-primary btn-sm btn-block mb-2" @click="showResi = true">
                    <i class="fas fa-truck"></i> Input Resi
                </button>
                <button v-if="order.payment_status === 'paid' && order.status !== 'completed' && order.status !== 'cancelled'" class="btn btn-outline-success btn-sm btn-block mb-2" @click="updateStatus('completed')">
                    <i class="fas fa-check-double"></i> Tandai Selesai
                </button>
                <button v-if="order.payment_status === 'paid' && order.status !== 'cancelled'" class="btn btn-warning btn-sm btn-block mb-2" @click="refundOrder">
                    <i class="fas fa-undo"></i> Refund
                </button>
                <button v-if="order.payment_status !== 'paid' && order.status !== 'cancelled'" class="btn btn-outline-danger btn-sm btn-block" @click="cancelOrder">
                    <i class="fas fa-times"></i> Batalkan
                </button>
            </div>

            <div v-if="showResi" class="mt-3 p-3 border rounded">
                <h6>Input Resi</h6>
                <input v-model="resiForm.courier" class="form-control form-control-sm mb-2" placeholder="Kurir (JNE/J&T)" />
                <input v-model="resiForm.service" class="form-control form-control-sm mb-2" placeholder="Service (REG/YES)" />
                <input v-model="resiForm.tracking" class="form-control form-control-sm mb-2" placeholder="Nomor Resi" />
                <button class="btn btn-primary btn-sm mr-2" @click="submitResi">Simpan</button>
                <button class="btn btn-light btn-sm" @click="showResi = false">Batal</button>
            </div>
        </div>

        <div class="modal-footer border-0">
            <router-link :to="`/shop/${shopId}/order/${orderId}`" class="btn btn-outline-primary btn-sm mr-auto" @click.native="$emit('close')">
                <i class="fas fa-external-link-alt"></i> Halaman Penuh
            </router-link>
            <button type="button" class="btn btn-secondary btn-sm" @click="$emit('close')">Tutup</button>
        </div>
    </div>
</template>

<script>
import moment from 'moment';

export default {
    name: 'OrderDetailModal',
    props: {
        orderId: { type: Number, required: true },
        shopId: { type: Number, required: true },
    },
    data() {
        return {
            order: null,
            items: [],
            loading: true,
            showResi: false,
            resiForm: { courier: '', service: '', tracking: '' },
        };
    },
    watch: {
        orderId: { immediate: true, handler() { this.loadOrder(); } },
    },
    methods: {
        async loadOrder() {
            this.loading = true;
            try {
                const response = await this.$store.dispatch('shop_order/shop_order_detail', this.orderId);
                this.order = response.data.data.order;
                this.items = response.data.data.items || [];
            } catch (e) {
                this.$toast.error('Gagal memuat detail order');
            } finally {
                this.loading = false;
            }
        },
        async markPaid() {
            if (!confirm('Konfirmasi pembayaran manual?')) return;
            try {
                await this.$store.dispatch('shop_order/shop_order_mark_paid', this.orderId);
                this.$toast.success('Pembayaran dikonfirmasi');
                this.loadOrder();
                this.$emit('updated');
            } catch (e) { this.$toast.error(e.response?.data?.message || 'Gagal'); }
        },
        async updateStatus(status) {
            try {
                await this.$store.dispatch('shop_order/shop_order_update_status', { id: this.orderId, status });
                this.$toast.success('Status diperbarui');
                this.loadOrder();
                this.$emit('updated');
            } catch (e) { this.$toast.error(e.response?.data?.message || 'Gagal'); }
        },
        async submitResi() {
            if (!this.resiForm.courier || !this.resiForm.tracking) {
                this.$toast.error('Kurir dan nomor resi wajib diisi');
                return;
            }
            try {
                await this.$store.dispatch('shop_order/shop_order_input_resi', { id: this.orderId, payload: this.resiForm });
                this.$toast.success('Resi disimpan');
                this.showResi = false;
                this.loadOrder();
                this.$emit('updated');
            } catch (e) { this.$toast.error(e.response?.data?.message || 'Gagal'); }
        },
        async cancelOrder() {
            const reason = prompt('Alasan pembatalan?');
            if (reason === null) return;
            try {
                await this.$store.dispatch('shop_order/shop_order_cancel', { id: this.orderId, reason });
                this.$toast.success('Order dibatalkan');
                this.loadOrder();
                this.$emit('updated');
            } catch (e) { this.$toast.error(e.response?.data?.message || 'Gagal'); }
        },
        async refundOrder() {
            const reason = prompt('Alasan refund?');
            if (reason === null) return;
            if (!confirm('Yakin refund? Saldo toko akan dikurangi.')) return;
            try {
                await this.$store.dispatch('shop_order/shop_order_refund', { id: this.orderId, reason });
                this.$toast.success('Refund berhasil');
                this.loadOrder();
                this.$emit('updated');
            } catch (e) { this.$toast.error(e.response?.data?.message || 'Gagal'); }
        },
        shortRef(ref) { return ref && ref.length > 20 ? ref.slice(0, 12) + '...' : ref; },
        formatRupiah(v) { return 'Rp ' + (v || 0).toLocaleString('id-ID'); },
        formatTime(t) { return t ? moment(parseInt(t)).format('DD MMM YYYY HH:mm') : '-'; },
        statusLabel(s) { return { pending: 'Pending', processed: 'Diproses', processing: 'Diproses', shipped: 'Dikirim', completed: 'Selesai', cancelled: 'Dibatalkan' }[s] || s; },
        paymentLabel(s) { return { pending: 'Belum Bayar', paid: 'Lunas', expired: 'Expired', failed: 'Gagal', refund: 'Refund' }[s] || s; },
        statusBadge(s) {
            return ['badge', { 'badge-warning': s === 'pending', 'badge-info': s === 'processed' || s === 'processing', 'badge-primary': s === 'shipped', 'badge-success': s === 'completed', 'badge-danger': s === 'cancelled' }];
        },
        paymentBadge(s) {
            return ['badge', { 'badge-warning': s === 'pending', 'badge-success': s === 'paid', 'badge-danger': s === 'failed' || s === 'expired', 'badge-secondary': s === 'refund' }];
        },
    },
};
</script>

<style scoped>
.info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
</style>
